/**
 * Copyright (c) 2024 rports (r), All rights reserved.
 *
 *     ______    _______  _______  ______    _______  _______ 
 *    |    _ |  |       ||       ||    _ |  |       ||       |
 *    |   | ||  |    _  ||   _   ||   | ||  |_     _||  _____|
 *    |   |_||_ |   |_| ||  | |  ||   |_||_   |   |  | |_____ 
 *    |    __  ||    ___||  |_|  ||    __  |  |   |  |_____  |
 *    |   |  | ||   |    |       ||   |  | |  |   |   _____| |
 *    |___|  |_||___|    |_______||___|  |_|  |___|  |_______|
 *
 * filename      : NavBarSideBar.jsx
 * description   : {{insert here}}
 * creation date : 10/03/2024
 * author        : "Luis CRUZ" <luis.cruz@rports.net>
 */



// --------------------------------------------------------------------------------
//      IMPORTS
// --------------------------------------------------------------------------------

import {
    useState,
    Fragment,
} from 'react';

import {
    View,
    Flex,
} from '@aws-amplify/ui-react';

import {
    useComponentContext,
    toolKit,
} from '@/components/core';

import {
    ModalLockBodyScroll,
} from '@/components/snippets/CustomDiv';

import {
    LinkText,
} from '@/components/snippets/RouterLink';

import {
    auth_routes,
} from '@/utils/router/core';

import {
    Cross as MenuIcon,
} from 'hamburger-react';

import {
    GoChevronRight as IconChevronRight,
} from 'react-icons/go';



// --------------------------------------------------------------------------------
//      FUNCTIONS
// --------------------------------------------------------------------------------

function NavBarSideBarItem({ classPrefix, to, isAuth, onClick, children, ...rest }) {
    // Component context
    const { ctx } = useComponentContext();

    function handleOnClick_signOut(event) {
        event.preventDefault();

        // Call the asynchronous process
        var proc_call = ctx.api.auth.signOut();

        // Wait for the result, then process it
        proc_call.then(proc_status => {
            // Unsuccessful call
            if (proc_status !== toolKit.enums.auth.procStatusTypes.SUCCESS) {
                console.error(proc_status);
            }

            // Propagate the original onClick action
            onClick();
        });
    }

    return (
        <Fragment>
            <Flex
                justifyContent='space-between'
                alignItems='center'
                alignContent='center'
                gap={ctx.theme.tokens.space.zero}
                width={ctx.theme.tokens.space.relative.full}
                className={`${classPrefix}-button-sidebar-container-item`}
            >
                {/* Left side */}
                <Flex
                    justifyContent='flex-start'
                    alignItems='center'
                    alignContent='center'
                    direction='row'
                    wrap='nowrap'
                    gap={ctx.theme.tokens.space.zero}
                >
                    <View>
                        {/* Is an authentication action (sign in, sign out) */}
                        {(isAuth) &&
                            <Fragment>
                                {/* Is authenticated */}
                                {(ctx.auth.state === toolKit.enums.auth.stateTypes.AUTHENTICATED) &&
                                    <Fragment>
                                        <View
                                            onClick={handleOnClick_signOut}
                                            style={{ cursor: 'pointer' }}
                                            className={`${classPrefix}-button-sidebar-container-item-content`}
                                            {...rest}
                                        >
                                            {/* TODO: Translate message */}
                                            Sign Out
                                        </View>
                                    </Fragment>
                                }

                                {/* Is NOT authenticated */}
                                {(ctx.auth.state === toolKit.enums.auth.stateTypes.UNAUTHENTICATED) &&
                                    <Fragment>
                                        <LinkText
                                            to={
                                                ctx.api.utils.buildUrlAuth(
                                                    auth_routes.SIGN_IN,
                                                    true
                                                )
                                            }
                                            onClick={onClick}
                                            className={`${classPrefix}-button-sidebar-container-item-content`}
                                            {...rest}
                                        >
                                            {/* TODO: Translate message */}
                                            Sign In
                                        </LinkText>
                                    </Fragment>
                                }
                            </Fragment>
                        }

                        {/* Is NOT an authentication */}
                        {(!isAuth) &&
                            <Fragment>
                                <LinkText
                                    to={to}
                                    onClick={onClick}
                                    className={`${classPrefix}-button-sidebar-container-item-content`}
                                    {...rest}
                                >
                                    {children}
                                </LinkText>
                            </Fragment>
                        }
                    </View>
                </Flex>

                {/* Right side */}
                <Flex
                    justifyContent='flex-end'
                    alignItems='center'
                    alignContent='center'
                    direction='row'
                    wrap='nowrap'
                    gap={ctx.theme.tokens.space.xs}
                    className={`${classPrefix}-button-sidebar-container-item-icon`}
                >
                    <IconChevronRight
                        size={ctx.theme.tokens.space.large.value}
                    />
                </Flex>
            </Flex>
        </Fragment>
    );
}

function NavBarSideBar({ classPrefix, itemsTop, itemsBottom, children, ...rest }) {
    // Component context
    const { ctx } = useComponentContext();

    // State variables
    const [isOpen, set_isOpen] = useState(false);

    return (
        <Fragment>
            <View
                className={`${classPrefix}-button-sidebar ${isOpen === true ? 'active' : ''}`}
            >
                <MenuIcon
                    direction='left'
                    size={20}
                    toggled={isOpen}
                    toggle={set_isOpen}
                    hideOutline={true}
                />

                {(isOpen) &&
                    <ModalLockBodyScroll>
                        <Fragment>
                            <View
                                {...rest}
                                className={`${classPrefix}-button-sidebar-container ${isOpen === true ? 'active' : ''}`}
                            >
                                <Flex
                                    direction='column'
                                    justifyContent='space-between'
                                    alignItems='flex-start'
                                    alignContent='flex-start'
                                    gap={ctx.theme.tokens.space.zero}
                                    height={ctx.theme.tokens.space.relative.full}
                                    paddingTop={ctx.theme.tokens.space.zero}
                                    paddingBottom={ctx.theme.tokens.space.xxl}
                                    paddingRight={ctx.theme.tokens.space.xxl}
                                    paddingLeft={ctx.theme.tokens.space.xxl}
                                >
                                    {/* Top side */}
                                    <Flex
                                        justifyContent='flex-start'
                                        alignItems='flex-start'
                                        alignContent='flex-start'
                                        direction='column'
                                        wrap='nowrap'
                                        gap={ctx.theme.tokens.space.medium}
                                        width={ctx.theme.tokens.space.relative.full}
                                    >
                                        {itemsTop.map((item, i) => {
                                            const { label, isAuth, ...rest } = item;

                                            return (
                                                <Fragment
                                                    key={i}
                                                >
                                                    <NavBarSideBarItem
                                                        classPrefix={classPrefix}
                                                        onClick={() => set_isOpen(false)}
                                                        isAuth={isAuth}
                                                        {...rest}
                                                    >
                                                        {label}
                                                    </NavBarSideBarItem>
                                                </Fragment>
                                            );
                                        })}
                                    </Flex>

                                    {/* Bottom side */}
                                    <Flex
                                        justifyContent='flex-end'
                                        alignItems='flex-start'
                                        alignContent='flex-start'
                                        direction='column'
                                        wrap='nowrap'
                                        gap={ctx.theme.tokens.space.medium}
                                        width={ctx.theme.tokens.space.relative.full}
                                    >
                                        {itemsBottom.map((item, i) => {
                                            const { label, isAuth, ...rest } = item;

                                            return (
                                                <Fragment
                                                    key={i}
                                                >
                                                    <NavBarSideBarItem
                                                        classPrefix={classPrefix}
                                                        onClick={() => set_isOpen(false)}
                                                        isAuth={isAuth}
                                                        {...rest}
                                                    >
                                                        {label}
                                                    </NavBarSideBarItem>
                                                </Fragment>
                                            );
                                        })}
                                    </Flex>
                                </Flex>
                            </View>
                        </Fragment>
                    </ModalLockBodyScroll>
                }
            </View>
        </Fragment>
    );
}



// --------------------------------------------------------------------------------
//      EXPORTED OBJECTS
// --------------------------------------------------------------------------------

export {
    NavBarSideBar,
};



// End of file
